import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import { Button, Modal, Spin } from "antd";
import { Link } from "react-router-dom";

import Icons from "components/layout/icons";
import Localize from "components/service/localize";
import {
    ATTENDANCE_ACTIONS,
    useAttendanceFeedbackAudio,
    useAttendanceSession
} from "components/attendance/hooks";
import AttendanceScannerModal from "components/attendance/modals/attendance-scanner-modal";


const COMPACT_STATUS_CLASS = "is-compact";
const STATUS_CHECK_TIMEOUT = 10000;
const STATUS_NOTICES = {
    closed: {
        title: "ATTENDANCE.Result_ShiftClosed_Title",
        text: "ATTENDANCE.Result_ShiftClosed_Text"
    },
    checkedIn: {
        title: "ATTENDANCE.Result_AlreadyCheckedIn_Title",
        text: "ATTENDANCE.Result_AlreadyCheckedIn_Text"
    },
    error: {
        text: "ATTENDANCE.Error_StatusLoad_Text"
    }
};

const AttendanceStatusBlock = ({ eeId, moment }) => {
    const statusCopyRef = useRef(null);
    const statusCheckRef = useRef(null);
    const [ checkingStatus, setCheckingStatus ] = useState(false);
    const [ notice, setNotice ] = useState(null);
    const [ scannerVisible, setScannerVisible ] = useState(false);
    const [ scannerAction, setScannerAction ] = useState(ATTENDANCE_ACTIONS.CHECK_IN);
    const audioFeedback = useAttendanceFeedbackAudio();
    const {
        currentSession,
        isCheckedIn,
        loading,
        error,
        refresh
    } = useAttendanceSession(eeId);

    const action = isCheckedIn ?
        ATTENDANCE_ACTIONS.CHECK_OUT :
        ATTENDANCE_ACTIONS.CHECK_IN;

    const closeScanner = useCallback(() => {
        setScannerVisible(false);
    }, []);

    useLayoutEffect(() => {
        setCheckingStatus(false);
        setNotice(null);
        setScannerVisible(false);

        return () => {
            window.clearTimeout(statusCheckRef.current?.timeout);
            statusCheckRef.current = null;
        };
    }, [ eeId ]);

    const openScanner = useCallback(async () => {
        if (!eeId || statusCheckRef.current || scannerVisible || notice) {
            return;
        }

        audioFeedback.unlockAudio();
        const attempt = {};

        statusCheckRef.current = attempt;
        setCheckingStatus(true);

        const finish = () => {
            window.clearTimeout(attempt.timeout);
            statusCheckRef.current = null;
            setCheckingStatus(false);
        };
        const fail = () => {
            if (statusCheckRef.current === attempt) {
                finish();
                setNotice(STATUS_NOTICES.error);
            }
        };

        attempt.timeout = window.setTimeout(fail, STATUS_CHECK_TIMEOUT);

        try {
            const freshSession = await refresh();

            if (statusCheckRef.current !== attempt) {
                return;
            }

            finish();

            if (freshSession.isCheckedIn !== isCheckedIn) {
                setNotice(freshSession.isCheckedIn ? STATUS_NOTICES.checkedIn : STATUS_NOTICES.closed);
                return;
            }

            setScannerAction(action);
            setScannerVisible(true);
        } catch (e) {
            fail();
        }
    }, [
        action,
        audioFeedback,
        eeId,
        isCheckedIn,
        notice,
        refresh,
        scannerVisible
    ]);

    useEffect(() => {
        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible" && eeId && !scannerVisible && !statusCheckRef.current) {
                refresh().catch(() => undefined);
            }
        };

        document.addEventListener("visibilitychange", refreshWhenVisible);

        return () => document.removeEventListener("visibilitychange", refreshWhenVisible);
    }, [ eeId, refresh, scannerVisible ]);

    const closeNotice = () => setNotice(null);

    const checkInMoment = currentSession?.check_in_at && moment ?
        moment(currentSession.check_in_at) :
        null;
    const checkInDate = checkInMoment ? checkInMoment.format("DD.MM") : "";
    const checkInTime = checkInMoment ? checkInMoment.format("HH:mm") : "";

    useLayoutEffect(() => {
        const statusCopy = statusCopyRef.current;

        if (!statusCopy) {
            return undefined;
        }

        const fitStatusText = () => {
            statusCopy.classList.remove(COMPACT_STATUS_CLASS);

            if (!isCheckedIn || !statusCopy.clientHeight) {
                return;
            }

            const statusText = statusCopy.querySelector("strong");

            if (!statusText) {
                return;
            }

            const textStyle = window.getComputedStyle(statusText);
            const lineHeight = parseFloat(textStyle.lineHeight) || 16;
            const textHeight = statusText.getBoundingClientRect().height;

            if (textHeight > (lineHeight * 2) + 1) {
                statusCopy.classList.add(COMPACT_STATUS_CLASS);
            }
        };

        fitStatusText();

        const resizeObserver = window.ResizeObserver ?
            new window.ResizeObserver(fitStatusText) :
            null;
        const mutationObserver = window.MutationObserver ?
            new window.MutationObserver(fitStatusText) :
            null;

        resizeObserver?.observe(statusCopy);
        mutationObserver?.observe(statusCopy, {
            childList: true,
            characterData: true,
            subtree: true
        });
        window.addEventListener("resize", fitStatusText);

        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            window.removeEventListener("resize", fitStatusText);
            statusCopy.classList.remove(COMPACT_STATUS_CLASS);
        };
    }, [
        checkInDate,
        checkInTime,
        isCheckedIn
    ]);

    return (
        <>
            <section
                className={ `attendance-status-block${ isCheckedIn ? " is-checked-in" : "" }` }
                aria-live="polite"
            >
                <div ref={ statusCopyRef } className="attendance-status-copy">
                    {loading ? (
                        <Spin size="small" />
                    ) : error ? (
                        <span className="attendance-status-error">
                            <Localize>ATTENDANCE.Error_StatusLoad_Text</Localize>
                        </span>
                    ) : isCheckedIn ? (
                        <>
                            <span className="attendance-status-check">
                                <Icons.AttendanceSuccess />
                            </span>
                            <strong>
                                <Localize date={ checkInDate } time={ checkInTime }>
                                    ATTENDANCE.Result_CheckedInAt
                                </Localize>
                            </strong>
                        </>
                    ) : (
                        <>
                            <span className="attendance-status-label">
                                <Localize>ATTENDANCE.Overview_NotCheckedIn</Localize>
                            </span>
                            <strong>
                                <Localize br={ <br /> }>
                                    ATTENDANCE.Overview_CheckInPrompt
                                </Localize>
                            </strong>
                        </>
                    )}
                </div>
                <Button
                    type="primary"
                    className="attendance-status-action"
                    icon={ <Icons.QrCode /> }
                    loading={ checkingStatus }
                    disabled={ checkingStatus || !eeId }
                    onClick={ openScanner }
                >
                    <Localize>
                        {isCheckedIn ?
                            "ATTENDANCE.Button_CheckOut" :
                            "ATTENDANCE.Button_CheckIn"}
                    </Localize>
                </Button>
            </section>
            <AttendanceScannerModal
                visible={ scannerVisible }
                action={ scannerAction }
                eeId={ eeId }
                moment={ moment }
                audioFeedback={ audioFeedback }
                onClose={ closeScanner }
            />
            <Modal
                className="modal-standard attendance-status-notice"
                visible={ Boolean(notice) }
                title={ notice?.title ? <Localize>{ notice.title }</Localize> : null }
                centered
                footer={ null }
                closable={ false }
                maskClosable={ false }
                onCancel={ closeNotice }
            >
                <p>
                    <Localize>{ notice?.text }</Localize>
                    {notice === STATUS_NOTICES.closed && (
                        <Link
                            className="attendance-status-records-link"
                            to="/more/my-time-records"
                            onClick={ closeNotice }
                        >
                            <Localize>ATTENDANCE.Link_MyTimeRecords</Localize>
                        </Link>
                    )}
                </p>
                <Button type="primary" block onClick={ closeNotice }>
                    <Localize>ATTENDANCE.Button_GotIt</Localize>
                </Button>
            </Modal>
        </>
    );
};

export default AttendanceStatusBlock;
